// KIADÁS-KATEGÓRIÁK kezelője — beúszó sáv, benne a KÉT SZINTŰ fa és egy felvevő/szerkesztő űrlap.
//
// A fa mélységét a szerver zárja (alkategória alá újabb szint 422), a felület pedig őszinte tükör: a
// szülő-select CSAK fő-kategóriákat kínál. A törlés kapuja szintén szerver-oldali — a hivatkozott kategória
// törlése 422, mert az FK `ON DELETE SET NULL`, tehát a néma törlés az elvégzett besorolás-munkát vinné el.

import { MAX_CATEGORY_DEPTH, type ExpenseCategoryRow } from '@atlas/contracts';
import { UserIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BilingualField } from '@/components/shared/bilingual-field';
import { CatalogDeleteDialog, CatalogRow, CatalogSheet } from '@/components/shared/catalog-manager';
import { SelectField, SwitchField } from '@/components/shared/form-fields';
import { treeGuides } from '@/components/shared/tree-guides';
import { Form } from '@/components/ui/form';
import { categoryDepth, categoryLabel, expenseKeys } from '@/features/expenses/api';
import {
  buildCategoryResolver,
  categoryFormFrom,
  EMPTY_CATEGORY_FORM,
  ROOT_PARENT,
  toCategoryBody,
  type CategoryFormValues,
} from '@/features/expenses/form';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';

export interface CategoryManagerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: readonly ExpenseCategoryRow[];
  canManage: boolean;
  canDelete: boolean;
}

export function CategoryManagerSheet({
  open,
  onOpenChange,
  categories,
  canManage,
  canDelete,
}: CategoryManagerSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [editing, setEditing] = useState<ExpenseCategoryRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ExpenseCategoryRow | null>(null);

  const form = useForm<CategoryFormValues>({
    resolver: buildCategoryResolver(t),
    mode: 'onTouched',
    defaultValues: EMPTY_CATEGORY_FORM,
  });
  const { reset, handleSubmit, setError } = form;

  const startEdit = (row: ExpenseCategoryRow): void => {
    setEditing(row);
    reset(categoryFormFrom(row));
  };
  const startCreate = (): void => {
    setEditing(null);
    reset(EMPTY_CATEGORY_FORM);
  };

  const saveMutation = useEntityMutation<CategoryFormValues>({
    mutationFn: (values) =>
      editing
        ? api.expenses.categoryUpdate.mutation({
            params: { id: editing.id },
            body: toCategoryBody(values),
            headers: { 'if-match': String(editing.version) },
          })
        : api.expenses.categoryCreate.mutation({ body: toCategoryBody(values) }),
    // A `all` prefix EGY sorban éri el a listát, az összegzést és a kategória-fát — egy írás
    // mindhármat elavulttá teszi (a darabszámok és a havi összeg is mozdul).
    invalidateKeys: [expenseKeys.all],
    onFieldErrors: (field, messages) => {
      if (field === 'parentId') setError('parentId', { message: messages.join(' ') });
      if (field === 'name') setError('nameHu', { message: messages.join(' ') });
    },
    messages: {
      success: editing ? t('toast:expenses.categoryUpdated') : t('toast:expenses.categoryCreated'),
      conflict: t('toast:expenses.conflict'),
      forbidden: t('toast:expenses.forbidden'),
      // A 422 itt EGY dolgot jelenthet: a fa mélység-szabályát (a névhiba mező-szinten jelenik meg).
      validation: t('toast:expenses.categoryDepth'),
      error: t('toast:expenses.error'),
      unavailable: t('toast:expenses.unavailable'),
    },
  });

  const deleteMutation = useEntityMutation<{ id: string }>({
    mutationFn: ({ id }) => api.expenses.categoryRemove.mutation({ params: { id } }),
    // A `all` prefix EGY sorban éri el a listát, az összegzést és a kategória-fát — egy írás
    // mindhármat elavulttá teszi (a darabszámok és a havi összeg is mozdul).
    invalidateKeys: [expenseKeys.all],
    messages: {
      success: t('toast:expenses.categoryDeleted'),
      conflict: t('toast:expenses.conflict'),
      forbidden: t('toast:expenses.forbidden'),
      // A törlés EGYETLEN 422-je: a kategória használatban van.
      validation: t('toast:expenses.categoryInUse'),
      error: t('toast:expenses.error'),
      unavailable: t('toast:expenses.unavailable'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    saveMutation.mutate(values, {
      onSuccess: () => {
        setEditing(null);
        reset(EMPTY_CATEGORY_FORM);
      },
    });
  });

  /**
   * A LEHETSÉGES SZÜLŐK: minden csomópont, ami alá még KERÜLHET szint — tehát a főkategóriák (0) és az
   * alkategóriák (1). A tétel-szint (2) kimarad: alá a szerver úgyis 422-t adna (`MAX_CATEGORY_DEPTH`), és a
   * felület nem kínálhat olyat, amit a fal elutasít.
   *
   * A SAJÁT ÁG kimarad: önmaga és a leszármazottai nem lehetnek a szülője (az kört csinálna a fából). A
   * szerver az önmagára mutatást külön is tiltja — ez a felület tükre, nem a fal.
   */
  const descendantsOfEditing = (() => {
    if (!editing) return new Set<string>();
    const out = new Set<string>([editing.id]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const c of categories) {
        if (c.parentId !== null && out.has(c.parentId) && !out.has(c.id)) {
          out.add(c.id);
          grew = true;
        }
      }
    }
    return out;
  })();
  const parentOptions = [
    { value: ROOT_PARENT, label: t('expenses.categories.rootOption'), depth: 0 },
    ...categories
      .filter((c) => !descendantsOfEditing.has(c.id))
      // A BÉR TÍPUSÚ TÉTEL nem lehet szülő: alá vett gyerek gyűjtővé tenné, a jelölés pedig csak tételen
      // állhat. A szerver ugyanezt 422-vel zárja — ez a felület őszinte tükre.
      .filter((c) => !c.requiresEmployee)
      .map((c) => ({ ...c, depth: categoryDepth(c, categories) }))
      .filter((c) => c.depth < MAX_CATEGORY_DEPTH - 1)
      .map((c) => ({
        value: c.id,
        label: categoryLabel(c.name, locale),
        // A gyökér-opció a 0. szint, tehát a fa MINDEN csomópontja eggyel beljebb kezdődik nála.
        depth: c.depth + 1,
      })),
  ];

  /**
   * TÉTEL-E a szerkesztett csomópont (nincs gyereke). CSAK tételen áll a bér-jelölés — egy gyűjtőre kitéve
   * a szerver 422-t adna, és a felület nem kínálhat olyat, amit a fal elutasít.
   *
   * ÚJ kategóriánál (`editing === null`) mindig igaz: a friss csomópontnak definíció szerint nincs gyereke.
   */
  const editingIsLeaf = editing === null || !categories.some((c) => c.parentId === editing.id);

  // A VEZETŐVONALAK egyszer, az EGÉSZ listára (a mélység-térképpel együtt): soronként újraszámolva a
  // fa-járás négyzetes lenne, és ugyanazt a választ adná.
  const depthOf = (row: ExpenseCategoryRow): number => categoryDepth(row, categories);
  const guides = treeGuides(categories, depthOf);

  return (
    <>
      <CatalogSheet
        open={open}
        onOpenChange={onOpenChange}
        title={t('expenses.categories.title')}
        description={t('expenses.categories.desc')}
        canManage={canManage}
        onSubmit={onSubmit}
        busy={saveMutation.isPending}
        editing={editing !== null}
        scrollToFormKey={editing}
        onCancelEdit={startCreate}
        labels={{
          add: t('expenses.categories.add'),
          saveEdit: t('expenses.categories.saveEdit'),
          cancel: t('common.cancel'),
          newTitle: t('expenses.categories.newTitle'),
          editTitle: t('expenses.categories.editTitle'),
        }}
        isEmpty={categories.length === 0}
        emptyLabel={t('expenses.categories.empty')}
        form={
          <Form {...form}>
            <BilingualField<CategoryFormValues>
              variant="input"
              label={t('expenses.categories.name')}
              nameHu="nameHu"
              nameEn="nameEn"
              required
            />
            <SelectField<CategoryFormValues>
              name="parentId"
              label={t('expenses.categories.parent')}
              hint={t('expenses.categories.parentHint')}
              options={parentOptions}
            />
            {/* BÉR TÍPUSÚ TÉTEL. CSAK levélen áll (l. `editingIsLeaf`), és NEM öröklődik: a jelölés ott
                ül, ahová a kiadás is sorolható — a munkatárs-választó mindenhol ezt az EGY mezőt kérdezi. */}
            {editingIsLeaf && (
              <SwitchField<CategoryFormValues>
                name="requiresEmployee"
                label={t('expenses.categories.requiresEmployee')}
                hint={t('expenses.categories.requiresEmployeeHint')}
              />
            )}
          </Form>
        }
      >
        {categories.map((row, i) => (
          <CatalogRow
            key={row.id}
            guides={guides[i] ?? []}
            label={categoryLabel(row.name, locale)}
            emphasized={row.parentId === null}
            // JEL, nem badge: a `DomainBadge`-katalógus enum-fogalmakra való, ez pedig egyetlen
            // logikai jelölő — a felirat a képernyőolvasónak megy.
            trailing={
              row.requiresEmployee ? (
                <UserIcon
                  className="text-subtle size-4 shrink-0"
                  title={t('expenses.categories.employeeBadge')}
                />
              ) : undefined
            }
            count={row.expenseCount}
            editLabel={t('expenses.categories.editAria', { name: categoryLabel(row.name, locale) })}
            deleteLabel={t('expenses.categories.deleteAria', {
              name: categoryLabel(row.name, locale),
            })}
            {...(canManage ? { onEdit: () => startEdit(row) } : {})}
            {...(canDelete ? { onDelete: () => setDeleteTarget(row) } : {})}
          />
        ))}
      </CatalogSheet>

      <CatalogDeleteDialog
        targetLabel={deleteTarget ? categoryLabel(deleteTarget.name, locale) : null}
        onClose={() => setDeleteTarget(null)}
        title={t('expenses.categories.deleteTitle')}
        describe={(name) => t('expenses.categories.deleteDesc', { name })}
        pending={deleteMutation.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMutation.mutate(
            { id: deleteTarget.id },
            { onSettled: () => setDeleteTarget(null) },
          );
        }}
      />
    </>
  );
}
